import { createTheme } from "@mui/material/styles";
import {
  THEME_COLORS,
  THEME_BACKGROUNDS,
  THEME_TEXT,
  THEME_MODES,
  FONT_SIZE_OPTIONS,
} from "../constants/theme";

export const $orange = THEME_COLORS.ORANGE;
export const $blue = THEME_COLORS.BLUE;
export const $blueUnsafe = THEME_COLORS.BLUE_UNSAFE;
export const $lightBlueUnsafe = THEME_COLORS.LIGHT_BLUE_UNSAFE;
export const $lightGrey = THEME_COLORS.LIGHT_GREY;
export const $darkGrey = THEME_COLORS.DARK_GREY;
export const $inputText = THEME_COLORS.INPUT_TEXT;
export const $white = THEME_COLORS.WHITE;
export const $red = THEME_COLORS.RED;

const APP_FONT_SIZES = {
  [FONT_SIZE_OPTIONS.TINY]: 11,
  [FONT_SIZE_OPTIONS.SMALL]: 13,
  [FONT_SIZE_OPTIONS.MEDIUM]: 14,
  [FONT_SIZE_OPTIONS.LARGE]: 16,
};

// Create a theme that accepts mode parameter
export const createAppTheme = (
  mode = THEME_MODES.LIGHT,
  fontSizeOption = FONT_SIZE_OPTIONS.MEDIUM,
) => createTheme({
  typography: {
    fontSize: APP_FONT_SIZES[fontSizeOption] || APP_FONT_SIZES[FONT_SIZE_OPTIONS.MEDIUM],
  },
  palette: {
    mode,
    primary: {
      main: THEME_COLORS.ORANGE,
    },
    secondary: {
      main: THEME_COLORS.BLUE,
    },
    ...(mode === THEME_MODES.DARK && {
      background: {
        default: THEME_BACKGROUNDS.DARK.DEFAULT,
        paper: THEME_BACKGROUNDS.DARK.PAPER,
      },
      text: {
        primary: THEME_TEXT.DARK.PRIMARY,
        secondary: THEME_TEXT.DARK.SECONDARY,
      },
    }),
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontSize: "14px",
        textAlign: "center",
        whiteSpace: "pre-line",
      },
    },
    MuiButton: {
      root: {
        color: $white,
        backgroundColor: $orange,
        border: `2px solid ${$orange}`,
        "&:not(.MuiPickersToolbarButton-toolbarBtn)": {
          minHeight: "45px",
          minWidth: "130px",
          width: "inherit",
          padding: "0 16px",
          fontWeight: "700",
          borderRadius: "25px",
          textAlign: "center",
          // fontSize: '19px', // No need for this if orange is darker (as above)
        },
        "&:hover": {
          color: $orange,
          backgroundColor: $white,
        },
        "&$disabled": {
          opacity: 0.5,
          backgroundColor: $darkGrey,
          borderColor: $darkGrey,
          color: $white,
        },
      },
      outlined: {
        padding: "6px 20px",
        color: "#000",
        backgroundColor: $white,
        border: `2px solid ${$orange}`,
        textDecoration: "none",
        "&:hover": {
          color: $white,
          backgroundColor: $orange,
        },
      },
      text: {
        padding: "6px 20px",
        "& a": {
          color: $white,
          textDecoration: "none",
        },
      },
      sizeSmall: {
        "&:not(.MuiPickersToolbarButton-toolbarBtn)": {
          padding: "4px 8px",
          height: "initial",
          minHeight: "initial",
          minWidth: "70px",
        },
      },
    },
    MuiFormControl: {
      root: {
        width: "100%",
      },
    },
    MuiInputBase: {
      input: {
        display: "block",
        height: "54px",
        padding: "0 16px",
        backgroundColor: $lightGrey,
        borderRadius: "24px",
        border: "none",
      },
    },
    MuiSelect: {
      outlined: {
        borderRadius: "24px",
        "&:focus": {
          borderRadius: "24px",
        },
      },
      selectMenu: {
        whiteSpace: "initial",
      },
    },
    MuiOutlinedInput: {
      root: {
        "&:hover:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
          {
            borderColor: $inputText,
          },
      },
      input: {
        padding: "18px 40px",
      },
      multiline: {
        padding: "15px 0",
        backgroundColor: $lightGrey,
        borderRadius: "11px",
        "& .MuiOutlinedInput-input": {
          borderRadius: "11px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderRadius: "11px",
        },
      },
      inputMultiline: {
        padding: "36px 33px;",
      },
      notchedOutline: {
        borderWidth: "2px",
        borderRadius: "24px",
        borderColor: $lightGrey,
        "& > legend": {
          marginLeft: "26px",
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: "16px",
        color: "rgba(0, 0, 0, 0.75)",
        "&.Mui-focused": {
          color: "#5e626d",
        },
        "&.Mui-error": {
          color: $red,
        },
      },
    },
    MuiInputLabel: {
      outlined: {
        transform: "translate(40px, 20px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(40px, -6px) scale(0.75)",
        },
      },
    },
    MuiChip: {
      label: {
        whiteSpace: "normal",
      },
    },
    MuiSwitch: {
      root: {
        width: 28,
        height: 16,
        padding: 0,
        display: "flex",
      },
      switchBase: {
        padding: 2,
        color: "rgba(0, 0, 0, 0.65)",
        "&$checked": {
          transform: "translateX(12px)",
          color: $white,
          "&:not($disabled) + $track": {
            opacity: 1,
            backgroundColor: $blue,
            borderColor: $blue,
          },
        },
        "&$colorSecondary$checked:not($disabled)": {
          color: $white,
        },
      },
      thumb: {
        width: 12,
        height: 12,
        boxShadow: "none",
      },
      track: {
        border: `1px solid rgba(0, 0, 0, 0.54)`,
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: $white,
      },
    },
    MuiTabs: {
      root: {
        marginBottom: 20,
      },
      indicator: {
        display: "none",
      },
      flexContainer: {
        flexWrap: "wrap",
      },
    },
    MuiTab: {
      root: {
        borderRadius: "25px",
        transition: "all 0.3s ease-in-out",
        "&$selected": {
          color: $white,
          backgroundColor: $orange,
        },
      },
    },
    MuiAccordion: {
      root: {
        margin: 0,
        padding: 0,
        border: "none",
        boxShadow: "none",
        "&:not(:last-child)": {
          borderBottom: 0,
        },
        "&:before": {
          display: "none",
        },
        "&$expanded": {
          margin: "auto",
        },
      },
      expanded: {},
    },
    MuiAccordionSummary: {
      root: {
        padding: 0,
      },
    },
    MuiAccordionDetails: {
      root: {
        padding: 0,
      },
    },
  },
});

// Legacy default theme (light mode)
export const theme = createAppTheme(THEME_MODES.LIGHT);
